import { Injectable, inject } from '@angular/core';
import pkg from '../../../package.json';
import { LOCAL_STORAGE_KEYS } from '../config/consts';
import { UserWalletsData } from '../types/user-wallets-data';
import { EncryptionService } from './encryption.service';
import { UtilsHelper } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class PasswordManagerService {
  private readonly encryptionService = inject(EncryptionService);
  private utils = inject(UtilsHelper);

  private password: string | null = null;

  isUserHasSavedPassword(): boolean {
    return !!localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA);
  }

  async setSavedPassword(
    password: string,
    force: boolean = false,
  ): Promise<void> {
    if (localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA) && !force) {
      throw new Error('Password already set');
    }

    await this.saveWalletsData(this.getInitializedWalletsData(), password);
  }

  async checkPassword(password: string): Promise<boolean> {
    return !!(await this.getUserDataWithPassword(password));
  }

  importFromEncryptedData(encryptedUserData: string): boolean {
    const encryptedMessage = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA);

    if (encryptedMessage) {
      throw new Error('Data already found');
    }

    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_DATA, encryptedUserData);

    return true;
  }

  async getUserDataWithPassword(
    password: string,
  ): Promise<UserWalletsData | null> {
    const encryptedMessage = localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA);

    if (!encryptedMessage) {
      return null;
    }

    try {
      const shouldMigrateEncryptedData =
        this.encryptionService.isLegacyEncryptedData(encryptedMessage);
      const UserDataJson = await this.encryptionService.decrypt(
        encryptedMessage,
        password,
      );

      if (UserDataJson) {
        const userData: UserWalletsData = JSON.parse(UserDataJson);

        if (userData.version) {
          if (shouldMigrateEncryptedData) {
            await this.saveWalletsData(userData, password);
          }

          return userData;
        }

        return null;
      }
    } catch (error) {
      console.error('Error decrypting data:', error);
      return null;
    }

    return null;
  }

  async checkAndLoadPassword(password: string): Promise<boolean> {
    const userData = await this.getUserDataWithPassword(password);

    if (!userData) {
      return false;
    }

    this.password = password;

    return true;
  }

  async getUserData(): Promise<UserWalletsData> {
    if (!this.password) {
      throw new Error('Password not found');
    }

    const userData = await this.getUserDataWithPassword(this.password!);

    if (!userData) {
      throw new Error('User data not found');
    }

    return userData;
  }

  getInitializedWalletsData(): UserWalletsData {
    return {
      wallets: [],
      version: pkg.version,
      id: crypto.randomUUID(),
    };
  }

  async saveWalletsData(
    walletsData: UserWalletsData,
    password: string,
  ): Promise<boolean> {
    if (!password || this.utils.isNullOrEmptyString(password)) {
      return false;
    }

    const encryptedMessage = await this.encryptionService.encrypt(
      JSON.stringify(walletsData),
      password,
    );

    localStorage.setItem(LOCAL_STORAGE_KEYS.USER_DATA, encryptedMessage);
    return true;
  }

  async saveWalletsDataWithStoredPassword(
    walletsData: UserWalletsData,
  ): Promise<boolean> {
    if (!this.password) {
      return false;
    }

    return this.saveWalletsData(walletsData, this.password);
  }

  clearPassword(): void {
    this.password = null;
  }

  async clearAllData(): Promise<void> {
    localStorage.clear();
  }
}
